import { useAuth } from '@/contexts/AuthContext';
import { Navigate, Routes, Route } from 'react-router-dom';
import { SidebarProvider } from '@/components/ui/sidebar';
import { DashboardSidebar } from '@/components/dashboard/DashboardSidebar';
import { DashboardHeader } from '@/components/dashboard/DashboardHeader';
import { UserDashboard } from '@/components/dashboard/UserDashboard';
import { AgentDashboard } from '@/components/dashboard/AgentDashboard';
import { AgencyDashboard } from '@/components/dashboard/AgencyDashboard';
import { DeveloperDashboard } from '@/components/dashboard/DeveloperDashboard';
import { AdminDashboard } from '@/components/dashboard/AdminDashboard';
import { PendingApproval } from '@/components/dashboard/PendingApproval';
import { ProfileSettings } from '@/components/dashboard/ProfileSettings';
import { MyListings } from '@/components/dashboard/MyListings';
import { AddProperty } from '@/components/dashboard/AddProperty';
import { AddPlot } from '@/components/dashboard/AddPlot';
import { MyPlots } from '@/components/dashboard/MyPlots';
import { MyLeads } from '@/components/dashboard/MyLeads';
import { SavedProperties } from '@/components/dashboard/SavedProperties';
import { DashboardTransactions } from '@/components/dashboard/DashboardTransactions';
import { DashboardAnalytics } from '@/components/dashboard/DashboardAnalytics';
import { DashboardCustomers } from '@/components/dashboard/DashboardCustomers';
import { DashboardReviews } from '@/components/dashboard/DashboardReviews';
import { ManageProject } from '@/components/dashboard/ManageProject';
import { MyProjects } from '@/components/dashboard/MyProjects';
import { DeveloperApplications } from '@/components/dashboard/DeveloperApplications';
import { AgentApplications } from '@/components/dashboard/AgentApplications';
import { AgencyApplications } from '@/components/dashboard/AgencyApplications';
import { ProjectApprovals } from '@/components/dashboard/ProjectApprovals';
import { PropertyApprovals } from '@/components/dashboard/PropertyApprovals';
import { PlotApprovals } from '@/components/dashboard/PlotApprovals';
import { UserManagement } from '@/components/dashboard/UserManagement';
import { NotificationCenter } from '@/components/dashboard/NotificationCenter';
import { MessagingCenter } from '@/components/dashboard/MessagingCenter';
import { AddCommercialProperty } from '@/components/dashboard/AddCommercialProperty';
import { LeadPipeline } from '@/components/dashboard/LeadPipeline';
import { ClientManagement } from '@/components/dashboard/ClientManagement';
import { LeadAssignment } from '@/components/dashboard/LeadAssignment';
import { ProjectBookings } from '@/components/dashboard/ProjectBookings';
import { PlotMapping } from '@/components/dashboard/PlotMapping';
import { BookingTransfers } from '@/components/dashboard/BookingTransfers';
import { InventoryOverview } from '@/components/dashboard/InventoryOverview';
// TeamManagement consolidated into AgencyMembersPanel; legacy /team route now redirects.
import { AgencyMembersPanel } from '@/components/dashboard/AgencyMembersPanel';
import { AgencySettings } from '@/components/dashboard/AgencySettings';
import { AgencyBrandingForm } from '@/components/dashboard/AgencyBrandingForm';
import { AgencyLeadInbox } from '@/components/dashboard/AgencyLeadInbox';
import { Skeleton } from '@/components/ui/skeleton';
import { PermissionRoute } from '@/components/dashboard/PermissionRoute';

// Internal department dashboards
import { SalesAgentDashboard } from '@/components/dashboard/internal/SalesAgentDashboard';
import { SalesTeamLeaderDashboard } from '@/components/dashboard/internal/SalesTeamLeaderDashboard';
import { SalesManagerDashboard } from '@/components/dashboard/internal/SalesManagerDashboard';
import { MarketingMemberDashboard } from '@/components/dashboard/internal/MarketingMemberDashboard';
import { MarketingTeamLeaderDashboard } from '@/components/dashboard/internal/MarketingTeamLeaderDashboard';
import { MarketingManagerDashboard } from '@/components/dashboard/internal/MarketingManagerDashboard';
import { InternalTaskBoard } from '@/components/dashboard/internal/InternalTaskBoard';
import { ProspectPipeline } from '@/components/dashboard/internal/ProspectPipeline';
import { CampaignManager } from '@/components/dashboard/internal/CampaignManager';

import { InternalTeamsManager } from '@/components/dashboard/internal/InternalTeamsManager';
import { DepartmentManagement } from '@/components/dashboard/DepartmentManagement';
import { ITStaffDashboard } from '@/components/dashboard/internal/ITStaffDashboard';
import { ITManagerDashboard } from '@/components/dashboard/internal/ITManagerDashboard';
import { AccountingStaffDashboard } from '@/components/dashboard/internal/AccountingStaffDashboard';
import { HRStaffDashboard } from '@/components/dashboard/internal/HRStaffDashboard';
import { QualityControlDashboard } from '@/components/dashboard/internal/QualityControlDashboard';
import { AdOperationsDashboard } from '@/components/dashboard/internal/AdOperationsDashboard';
import { CustomerSupportDashboard } from '@/components/dashboard/internal/CustomerSupportDashboard';
import { LeadOpsDashboard } from '@/components/dashboard/internal/LeadOpsDashboard';
import { AuditQueue } from '@/components/dashboard/internal/AuditQueue';
import { FeaturedListingsManager } from '@/components/dashboard/internal/FeaturedListingsManager';
import { AdCampaignsManager } from '@/components/dashboard/internal/AdCampaignsManager';
import { AdvertisingPackagesManager } from '@/components/dashboard/internal/AdvertisingPackagesManager';
import { PackageInquiriesPanel } from '@/components/dashboard/internal/PackageInquiriesPanel';
import MyBanners from '@/pages/dashboard/MyBanners';
import { BannerPerformanceReport } from '@/components/dashboard/internal/BannerPerformanceReport';
import { BannerScheduleManager } from '@/components/dashboard/internal/BannerScheduleManager';
import { WebhookEventsViewer } from '@/components/dashboard/internal/WebhookEventsViewer';
import { BannerPlacementsManager } from '@/components/dashboard/internal/BannerPlacementsManager';
import BannerPreview from '@/pages/dashboard/BannerPreview';
import SupportTickets from '@/pages/dashboard/SupportTickets';
import { SearchRankingRulesManager } from '@/components/dashboard/internal/SearchRankingRulesManager';
import { FeaturedListingAnalytics } from '@/components/dashboard/internal/FeaturedListingAnalytics';
import MyInvoices from '@/pages/dashboard/MyInvoices';
import MyReceipts from '@/pages/dashboard/MyReceipts';
import SubscriptionCenter from '@/pages/dashboard/SubscriptionCenter';
import MyPackageRequests from '@/pages/dashboard/MyPackageRequests';
import CreditStore from '@/pages/dashboard/CreditStore';
import WalletPage from '@/pages/dashboard/Wallet';
import WalletReceipt from '@/pages/dashboard/WalletReceipt';
import WalletTopUpApprovals from '@/pages/dashboard/admin/WalletTopUpApprovals';
import ReceiptSettings from '@/pages/dashboard/admin/ReceiptSettings';
import CreditPurchasesLog from '@/pages/dashboard/admin/CreditPurchasesLog';
import AccountingCredentials from '@/pages/dashboard/admin/AccountingCredentials';
import StaffCredentials from '@/pages/dashboard/admin/StaffCredentials';
import PasswordRotationAuditLog from '@/pages/dashboard/admin/PasswordRotationAuditLog';
import PaymentMethodsManager from '@/pages/dashboard/admin/PaymentMethodsManager';
import FeaturedProjectsPage from '@/pages/dashboard/FeaturedProjects';
import EventsPage from '@/pages/dashboard/Events';
import RevenueDashboardPage from '@/pages/dashboard/RevenueDashboard';
import LeadMarketplaceConsole from '@/pages/dashboard/LeadMarketplaceConsole';
import MortgageReferrals from '@/pages/dashboard/MortgageReferrals';
import { ReportsQueue } from '@/components/dashboard/ReportsQueue';
import { AuditLog } from '@/components/dashboard/AuditLog';
import { DuplicateDetector } from '@/components/dashboard/DuplicateDetector';
import { BroadcastCenter } from '@/components/dashboard/BroadcastCenter';
import { ReviewsModeration } from '@/components/dashboard/ReviewsModeration';
import { EmailTemplates } from '@/components/dashboard/EmailTemplates';
import { FeaturedConsole } from '@/components/dashboard/FeaturedConsole';

import { SystemSettings } from '@/components/dashboard/SystemSettings';
import { FunnelDashboard } from '@/components/dashboard/insights/FunnelDashboard';
import { SearchInsights } from '@/components/dashboard/insights/SearchInsights';
import { AgencyScorecards } from '@/components/dashboard/insights/AgencyScorecards';
import { LandingCMS } from '@/components/dashboard/cms/LandingCMS';
import BlogAnalytics from '@/pages/dashboard/BlogAnalytics';
import NewsAdmin from '@/pages/dashboard/NewsAdmin';
import NewsEditor from '@/pages/dashboard/NewsEditor';
import NewsTaxonomy from '@/pages/dashboard/NewsTaxonomy';
import NewsAnalytics from '@/pages/dashboard/NewsAnalytics';
import NewsAuditLog from '@/pages/dashboard/NewsAuditLog';
import LandingAnalytics from '@/pages/dashboard/LandingAnalytics';
import StoriesManager from '@/pages/dashboard/StoriesManager';
import IntegrationsManager from '@/pages/dashboard/IntegrationsManager';
import DeveloperTeam from '@/pages/dashboard/developer/DeveloperTeam';
import DeveloperLeadRules from '@/pages/dashboard/developer/DeveloperLeadRules';
import DeveloperMyCrm from '@/pages/dashboard/developer/MyCrm';
import DeveloperOverview from '@/pages/dashboard/developer/DeveloperOverview';
import DeveloperCustomers from '@/pages/dashboard/developer/DeveloperCustomers';

export default function Dashboard() {
  const { user, loading, hasRole } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Skeleton className="h-12 w-48" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;

  const userType = user.user_metadata?.user_type;
  const isProfessionalPending = (type: string, role: string) => {
    return userType === type && !hasRole(role as any) && !hasRole('superadmin');
  };

  const getDefaultPanel = () => {
    if (hasRole('superadmin')) return <AdminDashboard />;
    // Internal roles
    if (hasRole('sales_manager')) return <SalesManagerDashboard />;
    if (hasRole('sales_team_leader')) return <SalesTeamLeaderDashboard />;
    if (hasRole('sales_agent')) return <SalesAgentDashboard />;
    if (hasRole('marketing_manager')) return <MarketingManagerDashboard />;
    if (hasRole('marketing_team_leader')) return <MarketingTeamLeaderDashboard />;
    if (hasRole('marketing_agent')) return <MarketingMemberDashboard />;
    // New department roles
    if (hasRole('it_manager')) return <ITManagerDashboard />;
    if (hasRole('it_staff')) return <ITStaffDashboard />;
    if (hasRole('accounting_manager' as any) || hasRole('accounting_staff')) return <AccountingStaffDashboard />;
    if (hasRole('hr_staff')) return <HRStaffDashboard />;
    if (hasRole('quality_control_lead') || hasRole('quality_control_agent')) return <QualityControlDashboard />;
    if (hasRole('ad_operations_lead') || hasRole('ad_operations_agent')) return <AdOperationsDashboard />;
    if (hasRole('customer_support_lead') || hasRole('customer_support_agent')) return <CustomerSupportDashboard />;
    if (hasRole('lead_ops_lead') || hasRole('lead_ops_agent')) return <LeadOpsDashboard />;
    // External roles
    if (isProfessionalPending('agent', 'agent')) return <PendingApproval applicationType="agent" />;
    if (isProfessionalPending('agency', 'agency_admin')) return <PendingApproval applicationType="agency" />;
    if (isProfessionalPending('developer', 'developer')) return <PendingApproval applicationType="developer" />;
    if (hasRole('developer')) return <DeveloperDashboard />;
    if (hasRole('agency_admin')) return <AgencyDashboard />;
    if (hasRole('agent')) return <AgentDashboard />;
    return <UserDashboard />;
  };

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-background">
        <DashboardSidebar />
        <div className="flex-1 flex flex-col min-w-0">
          <DashboardHeader />
          <main className="flex-1 p-4 md:p-6 overflow-auto">
            <Routes>
              <Route index element={getDefaultPanel()} />
              <Route path="listings" element={<PermissionRoute flag="canViewMyListings"><MyListings /></PermissionRoute>} />
              <Route path="properties/new" element={<PermissionRoute flag="canAddProperty"><AddProperty /></PermissionRoute>} />
              <Route path="properties/:id/edit" element={<PermissionRoute flag="canViewMyListings"><AddProperty /></PermissionRoute>} />
              <Route path="plots" element={<PermissionRoute flag="canViewMyListings"><MyPlots /></PermissionRoute>} />
              <Route path="plots/new" element={<PermissionRoute flag="canAddPlot"><AddPlot /></PermissionRoute>} />
              <Route path="leads" element={<PermissionRoute flag="canViewLeads"><MyLeads /></PermissionRoute>} />
              <Route path="saved" element={<SavedProperties />} />
              <Route path="profile" element={<ProfileSettings />} />
              <Route path="notifications" element={<NotificationCenter />} />
              <Route path="messages" element={<MessagingCenter />} />
              <Route path="support" element={<SupportTickets />} />
              <Route path="transactions" element={<DashboardTransactions />} />
              <Route path="analytics" element={<PermissionRoute flag="canViewAnalytics"><DashboardAnalytics /></PermissionRoute>} />
              <Route path="customers" element={<DashboardCustomers />} />
              <Route path="reviews" element={<PermissionRoute flag="canViewReviews"><DashboardReviews /></PermissionRoute>} />
              <Route path="projects/new" element={<PermissionRoute flag="canAddProject"><ManageProject /></PermissionRoute>} />
              <Route path="projects/:id/edit" element={<PermissionRoute flag="canViewMyProjects"><ManageProject /></PermissionRoute>} />
              <Route path="commercial/new" element={<PermissionRoute flag="canAddCommercial"><AddCommercialProperty /></PermissionRoute>} />
              <Route path="my-projects" element={<PermissionRoute flag="canViewMyProjects"><MyProjects /></PermissionRoute>} />
              <Route path="pipeline" element={<PermissionRoute flag="canViewLeadPipeline"><LeadPipeline /></PermissionRoute>} />
              <Route path="clients" element={<PermissionRoute flag="canManageClients"><ClientManagement /></PermissionRoute>} />
              <Route path="lead-assignment" element={<PermissionRoute flag="canAssignLeads"><LeadAssignment /></PermissionRoute>} />
              <Route path="bookings" element={<PermissionRoute flag="canManageBookings"><ProjectBookings /></PermissionRoute>} />
              <Route path="plot-map" element={<PermissionRoute flag="canManageBookings"><PlotMapping /></PermissionRoute>} />
              <Route path="transfers" element={<PermissionRoute flag="canManageBookings"><BookingTransfers /></PermissionRoute>} />
              <Route path="inventory" element={<PermissionRoute flag="canManageBookings"><InventoryOverview /></PermissionRoute>} />
              {/* Internal department routes */}
              <Route path="sales-tasks" element={<InternalTaskBoard assignedToMe />} />
              <Route path="sales-prospects" element={<ProspectPipeline />} />
              <Route path="sales-analytics" element={<DashboardAnalytics />} />
              <Route path="marketing-tasks" element={<InternalTaskBoard assignedToMe />} />
              <Route path="marketing-campaigns" element={<CampaignManager />} />
              <Route path="marketing-analytics" element={<DashboardAnalytics />} />
              {/* Legacy team routes → unified Internal Teams Manager */}
              <Route path="sales-team" element={<Navigate to="/dashboard/internal-teams" replace />} />
              <Route path="marketing-team" element={<Navigate to="/dashboard/internal-teams" replace />} />
              {/* IT, Accounting, HR routes */}
              <Route path="it-tasks" element={<InternalTaskBoard assignedToMe />} />
              <Route path="accounting-tasks" element={<InternalTaskBoard assignedToMe />} />
              <Route path="hr-tasks" element={<InternalTaskBoard assignedToMe />} />
              <Route path="audit-queue" element={<PermissionRoute flag="canAuditListings"><AuditQueue /></PermissionRoute>} />
              <Route path="featured-listings" element={<PermissionRoute flag="canManageFeatured"><FeaturedListingsManager /></PermissionRoute>} />
              <Route path="ad-campaigns" element={<PermissionRoute flag="canManageAdCampaigns"><AdCampaignsManager /></PermissionRoute>} />
              <Route path="advertising-packages" element={<PermissionRoute flag="canManageAdvertisingPackages"><AdvertisingPackagesManager /></PermissionRoute>} />
              <Route path="package-inquiries" element={<PermissionRoute flag="canManagePackageInquiries"><PackageInquiriesPanel /></PermissionRoute>} />
              <Route path="my-banners" element={<PermissionRoute flag="canManageMyBanners"><MyBanners /></PermissionRoute>} />
              <Route path="my-invoices" element={<MyInvoices />} />
              <Route path="my-receipts" element={<MyReceipts />} />
              <Route path="subscription" element={<SubscriptionCenter />} />
              <Route path="my-package-requests" element={<MyPackageRequests />} />
              <Route path="credit-store" element={<CreditStore />} />
              <Route path="wallet" element={<WalletPage />} />
              <Route path="wallet/receipt/:txId" element={<WalletReceipt />} />
              <Route path="wallet/receipt/sub/:refId" element={<WalletReceipt />} />
              <Route path="wallet-approvals" element={<WalletTopUpApprovals />} />
              <Route path="credit-purchases" element={<CreditPurchasesLog />} />
              <Route path="accounting-credentials" element={<AccountingCredentials />} />
              <Route path="staff-credentials" element={<StaffCredentials />} />
              <Route path="password-rotation-audit" element={<PasswordRotationAuditLog />} />
              <Route path="payment-methods" element={<PaymentMethodsManager />} />
              <Route path="receipt-settings" element={<ReceiptSettings />} />
              <Route path="developer/team" element={<DeveloperTeam />} />
              <Route path="developer/lead-rules" element={<DeveloperLeadRules />} />
              <Route path="developer/my-crm" element={<DeveloperMyCrm />} />
              <Route path="developer/overview" element={<DeveloperOverview />} />
              <Route path="developer/customers" element={<DeveloperCustomers />} />
              <Route path="featured-projects" element={<FeaturedProjectsPage />} />
              <Route path="events" element={<EventsPage />} />
              <Route path="revenue" element={<PermissionRoute flag="canViewRevenue"><RevenueDashboardPage /></PermissionRoute>} />
              <Route path="lead-marketplace" element={<PermissionRoute flag="canManageLeadMarketplace"><LeadMarketplaceConsole /></PermissionRoute>} />
              <Route path="mortgage-referrals" element={<PermissionRoute flag="canManageMortgageReferrals"><MortgageReferrals /></PermissionRoute>} />
              <Route path="banner-performance" element={<PermissionRoute flag="canManageAdCampaigns"><BannerPerformanceReport /></PermissionRoute>} />
              <Route path="banner-schedules" element={<PermissionRoute flag="canManageAdCampaigns"><BannerScheduleManager /></PermissionRoute>} />
              <Route path="banner-placements" element={<PermissionRoute flag="canManageAdCampaigns"><BannerPlacementsManager /></PermissionRoute>} />
              <Route path="banner-preview" element={<PermissionRoute flag="canManageAdCampaigns"><BannerPreview /></PermissionRoute>} />
              <Route path="webhook-events" element={<PermissionRoute flag="canManageAdCampaigns"><WebhookEventsViewer /></PermissionRoute>} />
              <Route path="search-ranking-rules" element={<PermissionRoute flag="canManageAdvertisingPackages"><SearchRankingRulesManager /></PermissionRoute>} />
              <Route path="featured-analytics" element={<PermissionRoute flag="canManageAdCampaigns"><FeaturedListingAnalytics /></PermissionRoute>} />

              {/* Insights (Phase 4) */}
              <Route path="insights/funnel" element={<PermissionRoute flag="canViewInsights"><FunnelDashboard /></PermissionRoute>} />
              <Route path="insights/search" element={<PermissionRoute flag="canViewInsights"><SearchInsights /></PermissionRoute>} />
              <Route path="insights/agencies" element={<PermissionRoute flag="canViewInsights"><AgencyScorecards /></PermissionRoute>} />
              <Route path="internal-teams" element={<InternalTeamsManager />} />
              <Route path="internal-team-setup" element={<Navigate to="/dashboard/internal-teams" replace />} />
              <Route path="sales-teams" element={<Navigate to="/dashboard/internal-teams" replace />} />
              <Route path="marketing-teams" element={<Navigate to="/dashboard/internal-teams" replace />} />
              <Route path="qc-team" element={<Navigate to="/dashboard/internal-teams" replace />} />
              {/* Listing approvals — QC team + superadmin (gated by canApproveListings) */}
              <Route path="property-approvals" element={<PermissionRoute flag="canApproveListings"><PropertyApprovals /></PermissionRoute>} />
              <Route path="plot-approvals" element={<PermissionRoute flag="canApproveListings"><PlotApprovals /></PermissionRoute>} />
              <Route path="project-approvals" element={<PermissionRoute flag="canApproveListings"><ProjectApprovals /></PermissionRoute>} />
              {/* Onboarding approvals — Sales team + superadmin (gated by canApproveApplications) */}
              <Route path="agent-applications" element={<PermissionRoute flag="canApproveApplications"><AgentApplications /></PermissionRoute>} />
              <Route path="agency-applications" element={<PermissionRoute flag="canApproveApplications"><AgencyApplications /></PermissionRoute>} />
              <Route path="developer-applications" element={<PermissionRoute flag="canApproveApplications"><DeveloperApplications /></PermissionRoute>} />
              {/* Agency-admin team management — Members panel now embeds permissions */}
              {(hasRole('agency_admin') || hasRole('superadmin')) && (
                <>
                  <Route path="team" element={<Navigate to="/dashboard/members" replace />} />
                  <Route path="members" element={<AgencyMembersPanel />} />
                  <Route path="agency-settings" element={<AgencySettings />} />
                  <Route path="agency-branding" element={<AgencyBrandingForm />} />
                  <Route path="agency-inbox" element={<AgencyLeadInbox />} />
                </>
              )}
              {hasRole('superadmin') && (
                <>
                  <Route path="admin/*" element={<AdminDashboard />} />
                  <Route path="users" element={<UserManagement />} />
                  <Route path="departments" element={<DepartmentManagement />} />
                  <Route path="reports" element={<ReportsQueue />} />
                  <Route path="audit-log" element={<AuditLog />} />
                  <Route path="duplicates" element={<DuplicateDetector />} />
                  <Route path="broadcasts" element={<BroadcastCenter />} />
                  <Route path="reviews-moderation" element={<ReviewsModeration />} />
                  <Route path="email-templates" element={<EmailTemplates />} />
                  <Route path="featured" element={<FeaturedConsole />} />
                  
                  <Route path="system-settings" element={<SystemSettings />} />
                  <Route path="integrations" element={<IntegrationsManager />} />
                </>
              )}
              {(hasRole('superadmin') || hasRole('marketing_manager') || hasRole('marketing_team_leader') || hasRole('marketing_agent')) && (
                <>
                  <Route path="cms" element={<LandingCMS />} />
                  <Route path="blog-analytics" element={<BlogAnalytics />} />
                  <Route path="news" element={<NewsAdmin />} />
                  <Route path="news/new" element={<NewsEditor />} />
                  <Route path="news/:id/edit" element={<NewsEditor />} />
                  <Route path="news/taxonomy" element={<NewsTaxonomy />} />
                  <Route path="news/analytics" element={<NewsAnalytics />} />
                  <Route path="news/audit" element={<NewsAuditLog />} />
                  <Route path="landing-analytics" element={<LandingAnalytics />} />
                  <Route path="stories" element={<StoriesManager />} />
                </>
              )}
            </Routes>
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}
